/* eslint-disable @wyz/module/no-relative-import */
import { Modal, notify } from '@wyz/ui';
import { useReducer } from 'react';
import {
  TransportItemPricingForm,
  TransportItemUploadForm,
} from '~/common/components';
import { Trans, useApplicationContext } from '~/common/context';
import { IImportedEan, PricingFormData } from '~/common/types';
import { useManageTransportItems } from '~/container-order/api';
import { ContainerTracking } from '~/container-order/helpers';
import type { IContainerOrder } from '~/common/types';
import { reducer } from './reducer';
type TransportItemImportModalProps = {
  onClose: () => void;
  orderId: string;
  item: IContainerOrder['resolvedItems'][number];
};

function TransportItemImportModal({
  onClose,
  item,
  orderId,
}: TransportItemImportModalProps) {
  const [state, dispatch] = useReducer(reducer, { type: 'UPLOAD' });
  const { auth } = useApplicationContext();
  const manageContainerOrders = useManageTransportItems({
    context: { userId: auth.userId as string },
    updateLines: { enabled: true },
  });

  const handleShowPricing = (
    importedItems: Array<IImportedEan>,
    file: File,
  ) => {
    dispatch({ type: 'SHOW_PRICING_TABLE', eans: importedItems, file });
  };

  const handleCancelPricing = () => {
    dispatch({ type: 'RESET' });
  };

  const handleOnSubmitPricing = (formData: PricingFormData) => {
    if (state.type !== 'PRICING') return;
    const pricesByEan = formData.eans.reduce(
      (acc, item) => {
        acc[item.ean] = item.price;
        return acc;
      },
      {} as Record<string, number>,
    );
    manageContainerOrders.updateLines.mutate(
      {
        orderId,
        itemId: item.uuid,
        body: {
          lines: state.eans.map((item) => ({
            ean: item.ean,
            quantity: Number(item.quantity),
            price: pricesByEan[item.ean],
          })),
        },
      },
      {
        onSuccess() {
          ContainerTracking.trackFileUpload();
          notify.success(<Trans i18nKey='app.common.messages.success' />, {
            id: 'toast-success-transport-item-import',
          });
          onClose();
        },
      },
    );
  };

  return (
    <Modal.Container open={true} onClose={onClose} size='lg' backdrop='static'>
      <Modal.Header>
        <Modal.Title>
          <Trans i18nKey='app.container.order.import_modal.title' />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {state.type === 'UPLOAD' && (
          <TransportItemUploadForm
            onClose={onClose}
            onShowPricing={handleShowPricing}
            itemId={item.uuid}
            orderId={orderId}
          />
        )}

        {state.type === 'PRICING' && (
          <TransportItemPricingForm
            eans={state.eans}
            onCancel={handleCancelPricing}
            onSubmit={handleOnSubmitPricing}
            isSubmitting={manageContainerOrders.updateLines.isLoading}
          />
        )}
      </Modal.Body>
    </Modal.Container>
  );
}

export default TransportItemImportModal;
